import { Injectable, inject } from '@angular/core';
import { AuthService } from '@brightside/core/auth';
import { AwsApiWrapperService } from '@brighthub/api';
import { FaActionSequence, StructuredLogger } from '@brighthub/logging';
import {
  ALL_STATUSES,
  Activity,
  ActivityStatus,
  CursorPage,
  ReviewAction,
} from '../models/activity.model';

/** Registered api-internal endpoint (see `environment.apiEndpoints`) that
 * fronts ai-activity-inbox-api. */
const API_NAME = 'api-internal';
const BASE_PATH = '/api/v1/activities';

/** The backend caps `limit` at 100 per request. */
const PAGE_LIMIT = 100;

export interface RtlPolicy {
  policy_id: string;
  dispositions: Record<string, string>;
  descriptions?: Record<string, string>;
  updated_at: string;
  updated_by?: string | null;
}

export interface ActivityFeed {
  /** Every activity retrieved for the client, newest first. */
  activities: Activity[];
  countsByStatus: Record<ActivityStatus, number>;
  /** True when the server reported another page but the cursor walk could not
   * continue, which means the counts are a floor rather than totals. */
  truncated: boolean;
}

/** One entry of FastAPI's 422 `detail` array. */
interface FastApiValidationError {
  msg?: string;
  loc?: (string | number)[];
}

interface HttpErrorLike {
  status?: number;
  error?: { detail?: string | FastApiValidationError[] };
  message?: string;
}

/**
 * Turn a rethrown `HttpErrorResponse` into something worth showing a user.
 *
 * FastAPI sends `detail` as a string for a raised `HTTPException` but as an
 * array of per-field errors for a 422, so the array has to be flattened or the
 * message renders as "[object Object]" and hides the only real diagnostic.
 */
export function describeHttpError(err: unknown): string {
  const httpErr = err as HttpErrorLike;
  const raw = httpErr?.error?.detail;
  const detail = Array.isArray(raw)
    ? raw
        .map((entry) =>
          [entry.loc?.join('.'), entry.msg].filter(Boolean).join(': '),
        )
        .join('; ')
    : raw;
  const status = httpErr?.status;
  if (detail && status) return `${status} ${detail}`;
  return detail ?? httpErr?.message ?? 'Unknown error';
}

/**
 * Read/write access to ai-activity-inbox-api through api-internal.
 *
 * Deliberately calls {@link AwsApiWrapperService} rather than the higher-level
 * `ApiService`: `ApiService` assumes every backend wraps its response as
 * `{ result: { status, code, ... } }` (the lambda-backed api-mobile
 * convention), and ai-activity-inbox-api is a plain FastAPI service returning
 * raw JSON, so `ApiService` throws inside its own pipeline even on a 200.
 * `AwsApiWrapperService` still injects the Okta bearer token and standard
 * headers, it just doesn't assume a response shape.
 *
 * Stateless by design — callers own the signals so a dialog and a list can hold
 * independent views of the same activity.
 */
@Injectable({ providedIn: 'root' })
export class ActivityInboxService {
  private readonly logs = inject(StructuredLogger);
  private readonly actionSequences = new Map<
    string,
    { sequence: FaActionSequence; created: number }
  >();
  private readonly _api = inject(AwsApiWrapperService);
  private readonly _auth = inject(AuthService);

  /**
   * Every activity for one client, across all statuses, for the Review tab on
   * that client's page.
   *
   * One cursor walk rather than one request per status: `client_guid` reads the
   * `by_client` GSI, which has no status in its key, so a single query already
   * spans every status and the counts fall out of bucketing it here. The
   * backend rejects `status` alongside `client_guid` for that reason.
   */
  async loadClientFeed(clientGuid: string): Promise<ActivityFeed> {
    const activities: Activity[] = [];
    const seenCursors = new Set<string>();
    let cursor: string | undefined;
    let truncated = false;

    while (true) {
      const queryParams: Record<string, string> = {
        client_guid: clientGuid,
        limit: String(PAGE_LIMIT),
      };
      if (cursor) queryParams['cursor'] = cursor;

      const page = await this._request<CursorPage<Activity>>(() =>
        this._api.get(API_NAME, BASE_PATH, { queryParams }),
      );
      activities.push(...page.items);

      if (!page.has_more) break;
      const nextCursor = page.next_cursor?.trim();
      if (!nextCursor || seenCursors.has(nextCursor)) {
        truncated = true;
        break;
      }
      seenCursors.add(nextCursor);
      cursor = nextCursor;
    }

    const countsByStatus = {} as Record<ActivityStatus, number>;
    for (const status of ALL_STATUSES) countsByStatus[status] = 0;
    for (const activity of activities) countsByStatus[activity.status] += 1;

    return {
      activities: activities.sort(
        (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
      ),
      countsByStatus,
      truncated,
    };
  }

  async loadConfirmationQueue(limit = 20): Promise<CursorPage<Activity>> {
    return this._request<CursorPage<Activity>>(() =>
      this._api.get(API_NAME, BASE_PATH, {
        queryParams: {
          status: 'needs_confirmation',
          limit: String(limit),
        },
      }),
    );
  }

  /** RTL policy document: dispositions plus optional per-type descriptions
   * used as action-chip tooltips. */
  async loadPolicy(): Promise<RtlPolicy> {
    return this._request<RtlPolicy>(() =>
      this._api.get(API_NAME, '/api/v1/policy'),
    );
  }

  /** Replace the RTL policy document (Administration → Review Tab Policy).
   * `descriptions` is always sent so tooltip copy updates with dispositions. */
  async savePolicy(policy: {
    dispositions: Record<string, string>;
    descriptions: Record<string, string>;
    expected_updated_at: string;
  }): Promise<RtlPolicy> {
    return this._request<RtlPolicy>(() =>
      this._api.put(API_NAME, '/api/v1/policy', {
        body: {
          dispositions: policy.dispositions,
          descriptions: policy.descriptions,
          updated_by: this._actor(),
          expected_updated_at: policy.expected_updated_at,
        },
      }),
    );
  }

  async getActivity(activityId: string): Promise<Activity> {
    return this._request<Activity>(() =>
      this._api.get(API_NAME, `${BASE_PATH}/${activityId}`),
    );
  }

  /** Appends a note without transitioning the activity. The backend treats PUT
   * and PATCH identically today — `note` is the only editable field — so this
   * uses PATCH. No UI calls this since the standalone "Save note" button came
   * out of the dialog; notes now ride along with confirm / retry / deny. */
  async addNote(activityId: string, note: string): Promise<Activity> {
    return this._request<Activity>(() =>
      this._api.patch(API_NAME, `${BASE_PATH}/${activityId}`, {
        body: { note, actor: this._actor() },
      }),
    );
  }

  /**
   * Confirm, retry, or deny. `confirm` hands the activity to the
   * activity-status state machine and returns it as `in_progress` — the machine
   * writes the terminal status, so the row is locked from here. `retry`
   * re-invokes the router agent with the note as feedback, and `deny` is a
   * terminal status change.
   */
  async applyAction(
    activityId: string,
    action: ReviewAction,
    note?: string,
  ): Promise<Activity> {
    // Shared by row and detail dialog; resource IDs stay in memory, never in logs.
    const key = `${activityId}:${action}`;
    const tracked =
      action === 'confirm' || action === 'deny' || action === 'retry';
    let sequence = this.actionSequences.get(key);
    if (tracked && (!sequence || Date.now() - sequence.created > 10 * 60_000)) {
      sequence = {
        sequence: this.logs.action(
          action === 'confirm'
            ? 'ai_review.activity.confirm'
            : action === 'retry'
              ? 'ai_review.activity.retry'
              : 'ai_review.activity.deny',
        ),
        created: Date.now(),
      };
      this.actionSequences.set(key, sequence);
      const oldest = this.actionSequences.keys().next().value;
      if (this.actionSequences.size > 128 && oldest !== undefined)
        this.actionSequences.delete(oldest);
    }
    const attempt = tracked ? sequence?.sequence.begin() : undefined;
    let dispatched = false;
    try {
      const actor = this._actor();
      dispatched = true;
      const result = await this._request<Activity>(() =>
        this._api.post(API_NAME, `${BASE_PATH}/${activityId}/actions`, {
          body: { action, note, actor },
        }),
      );
      attempt?.succeed(
        'acceptance',
        action === 'confirm' || action === 'retry',
      );
      this.actionSequences.delete(key);
      return result;
    } catch (error) {
      attempt?.fail(error, 'acceptance', dispatched);
      throw error;
    }
  }

  /** Okta display name for Past Notes. Blank when the profile hasn't hydrated
   * yet — the API then falls back to ``human_reviewer``. */
  private _actor(): string | undefined {
    const name = this._auth.userDisplayName().trim();
    return name || undefined;
  }

  /** `AwsApiWrapperService` resolves to `false` instead of throwing when the
   * Okta token isn't available yet, which would otherwise surface as a
   * confusing "cannot read property of false" further up. */
  private async _request<T>(send: () => Promise<unknown>): Promise<T> {
    const response = await send();
    if (response === false) {
      throw Object.assign(
        new Error(
          'Not signed in yet — no access token available for the request.',
        ),
        { status: 401 },
      );
    }
    return response as T;
  }
}
